// The PNR operation center's STEP BOARD — the pure, unit-testable half.
//
// The card used to scatter "what does this passenger already have" across six
// cards; the operator had no single picture of it. Now every step of the
// journey (flight · hotel · meals & card · transport · passenger) is derived
// ONCE into a `StepState` the tab board renders as a lamp + one-line value and
// the Overview ledger renders as ASSIGNED (what was decided for the passenger)
// vs DELIVERED (what they actually hold — a ticket, a held room, a loaded card,
// a dispatched ride, a sent offer). `deriveNextUp` collapses the same facts into
// the ONE thing to do now, so the dialog's action bar can carry a single
// contextual primary instead of buttons between the data.
//
// i18n: like `pnr-status.ts`, every string comes from the translator handed in
// LAST — this module holds KEYS (under `pnr:steps.*` / `pnr:nextUp.*`), never
// language. Tests pass an identity `t` and assert keys.

import type { DisruptionPnrGroupRow, RosterRebookingStatus } from '@/lib/disruptions/types';
import type { Reservation } from '@/lib/booking/types';
import type { TripView } from '@/lib/transport/types';
import { transportDestination } from '@/lib/transport/destination';
import type { INotificationStatusRow } from '@/lib/notifications/types';
import { passengerSeen } from '@/lib/notifications/engagement';
import { isTerminalTripStatus } from '@/lib/transport/types';
import { rosterVoucherCounts, voucherCoversTransport } from '@/lib/transport/voucher-coverage';
import type { GroupVoucherView } from '@/lib/transport/voucher-types';
import type { PurchasePhase } from '@/lib/recovery/recovery-model';
import type { BoardTone } from './airport-tokens';

/** The translator shape this module needs — `useTranslation('pnr').t` fits. */
export type PnrT = (key: string, opts?: Record<string, unknown>) => string;

export type StepKey = 'flight' | 'hotel' | 'meals' | 'transport' | 'passenger';

/** Journey order — the order the board and the ledger draw the steps in. */
export const STEP_ORDER: readonly StepKey[] = ['flight', 'hotel', 'meals', 'transport', 'passenger'];

export interface StepLine {
  primary: string;
  secondary?: string | undefined;
  /** ISO-8601 commercial acceptance deadline — the ledger renders a live clock
   *  (`AcceptanceClock`). Only a HELD hotel carries it; absent = no clock. */
  acceptanceExpiresAt?: string | undefined;
}

/**
 * The notification failure reasons the console has copy for
 * (`pnr:notification.current.failure.*`), mirroring the operator-bff
 * `NotificationFailureReason`. Since providers-by-service M3 (slices N3 + B3), a
 * send that our own boundary refused before any vendor call carries its own code
 * instead of `vendor_rejected`: `TEST_RECIPIENT_NOT_ALLOWED` (the recipient is not
 * on the test allowlist), `DEMO_ALLOWLIST_REQUIRED` (D17: a live send on a demo
 * deployment with no allowlist) and `CHANNEL_OFF` (the binding is switched off
 * outside demo).
 */
export const NOTIFICATION_FAILURE_REASONS = [
  'no_phone_on_file',
  'no_email_on_file',
  'contact_unavailable',
  'unsupported_channel',
  'TEST_RECIPIENT_NOT_ALLOWED',
  'DEMO_ALLOWLIST_REQUIRED',
  'CHANNEL_OFF',
  'vendor_rejected',
  'dispatch_failed',
] as const;

export type NotificationFailureReason = (typeof NOTIFICATION_FAILURE_REASONS)[number];

/** The reason to explain: a known code exactly as sent. Anything else (an unknown
 *  code, or free provider text) becomes `dispatch_failed` and is never shown verbatim. */
export function notificationFailureReason(raw: string | undefined): NotificationFailureReason {
  return (NOTIFICATION_FAILURE_REASONS as readonly string[]).includes(raw ?? '')
    ? (raw as NotificationFailureReason)
    : 'dispatch_failed';
}

/** The passenger's last DECLINE of the offer, when it carried their preference
 *  (`hotelDecline.*`) and/or their own words. Undefined for anything else. */
export function lastDecline(group: DisruptionPnrGroupRow): { intent?: string; reason?: string; hotel: boolean } | undefined {
  const r = group.lastOfferResponse;
  if (r?.response !== 'DECLINED') return undefined;
  if (group.subCaseStatus && group.subCaseStatus !== 'REJECTED_BY_PAX') return undefined;
  return {
    ...(r.declineIntent ? { intent: r.declineIntent } : {}),
    ...(r.reason?.trim() ? { reason: r.reason.trim() } : {}),
    hotel: r.declineScope === 'HOTEL',
  };
}

/** «Prefiero que me llamen» (§6.7): the passenger declined and asked to be called. */
export function callbackRequested(group: DisruptionPnrGroupRow): boolean {
  const r = group.lastOfferResponse;
  return r?.response === 'DECLINED' && r.declineIntent === 'CALLBACK_REQUESTED';
}

/** A phone may be on file (unknown counts as maybe — the call itself says if there is none). */
export function phoneOnFile(group: DisruptionPnrGroupRow): boolean {
  return (group.contactOnFile ?? group.passengers[0]?.contactOnFile)?.phone !== false;
}

const quoted = (text: string): string => `“${text}”`;

export interface StepState {
  key: StepKey;
  /** What the step's state MEANS (colour is the board's business). */
  tone: BoardTone;
  /** The lamp pulses while the step still wants something from someone. */
  pulse: boolean;
  /** One line under the tab label — the glance. */
  value: string;
  /** The chip word on the ledger row. */
  state: string;
  /** What was decided for the passenger. */
  assigned: StepLine;
  /** What the passenger actually holds. */
  delivered: StepLine;
  /** The card's ONE vocabulary for this step (Listo · En curso · Necesita decisión · No
   *  aplica · Pendiente). Absent = read from the tone (`tripPhaseOf`). */
  phase?: TripPhase | undefined;
}

/** The passenger card's one vocabulary (PO 2026-10-31, P7). */
export type TripPhase = 'done' | 'inProgress' | 'needsDecision' | 'notApplicable' | 'pending' | 'cancelled';

const TONE_PHASE: Record<BoardTone, TripPhase> = {
  ok: 'done',
  info: 'inProgress',
  warn: 'needsDecision',
  alert: 'needsDecision',
  idle: 'notApplicable',
};

/** The step's word in the card's vocabulary — its own when it set one, else its tone's. */
export function tripPhaseOf(step: Pick<StepState, 'phase' | 'tone'>): TripPhase {
  return step.phase ?? TONE_PHASE[step.tone];
}

/** The tone each vocabulary word is drawn in. */
export const PHASE_TONE: Record<TripPhase, BoardTone> = {
  done: 'ok',
  inProgress: 'info',
  needsDecision: 'warn',
  notApplicable: 'idle',
  pending: 'idle',
  cancelled: 'alert',
};

/** The wallet card facts the meals step reads (care board / case cards). */
export interface CardFacts {
  status: string;
  last4?: string | undefined;
  /** Face value + settled top-ups, minor units. */
  loadedMinor?: number | undefined;
  availableMinor?: number | undefined;
  currency: string;
  /** False when the wallet upstream did not answer — never fabricate a $0.00. */
  balancesKnown: boolean;
}

export interface StepsInput {
  group: DisruptionPnrGroupRow;
  liveStatus: RosterRebookingStatus;
  isSelected: boolean;
  isTicketed: boolean;
  ticketNumber?: string | undefined;
  /** Live ranked options count (0 while none are loaded). */
  optionsCount: number;
  /** Every option already left (`offerExpiryOf` kind `options`): nothing left to choose. */
  optionsExpired?: boolean | undefined;
  /** Human copy for a terminal no-options verdict, when a code is recognised. */
  noOptionsCopy?: string | undefined;
  /** Assigned-flight facts — live option first, the async denorm as fallback. */
  assignedFlightNumber?: string | undefined;
  /** "SCL → LIM" */
  assignedRoute?: string | undefined;
  /** Formatted departure of the assigned flight (airport TZ). */
  assignedWhen?: string | undefined;
  /** Formatted departure of the DISRUPTED flight. */
  disruptedWhen?: string | undefined;
  recovery?: { label: string; warn: boolean } | null | undefined;
  hotel: {
    reservation?: Reservation | undefined;
    /** Overnight verdict — a hotel is needed. */
    needs: boolean;
    /** Same-day re-accommodation — no overnight. */
    sameDay: boolean;
    /** "1 night · Aug 31 → Sep 01" when the stay is known. */
    stay?: string | undefined;
    /** Operator-declared stay-home (`passengerElection === 'LOCAL_RESIDENT'`):
     *  the passenger stays at their own home — calm, no hotel demand. */
    stayingHome?: boolean | undefined;
  };
  card?: CardFacts | undefined;
  /** Money formatter (minor units + currency) — injected so this stays pure. */
  formatMoney: (minor: number, currency: string) => string;
  trips: {
    /** This PNR's trips (live + terminal). */
    mine: readonly TripView[];
    loaded: boolean;
  };
  /** The group's Uber voucher as the drawer read it (`null` = none; `undefined` = not
   *  read yet — the roster's `transportVoucher` fact speaks meanwhile). A voucher IS
   *  ground transport: the passenger rides whenever they want. */
  voucher?: GroupVoucherView | null | undefined;
  notification?: INotificationStatusRow | undefined;
  /** Formatter for an ISO instant → short local time ("12:02"). */
  formatWhen: (iso: string | null | undefined) => string;
  /** The CASE is care-only (`rebookingDisabled`): every step is an OFFERING the
   *  operator MAY arrange, never an obligation — calm tones, no pulses, same
   *  actions. The wire facts (`requiresHotel` etc.) are untouched. */
  careOnly?: boolean | undefined;
  /** #771 — a CONFIRMED replacement purchase on the external (LCC) rail. The
   *  flight step reads it as resolved (ok, no pulse) instead of demanding
   *  options; a legacy ticket/selection still outranks it. */
  externalBooking?:
    | { provider: string; bookingReference?: string | undefined; mode?: string | undefined }
    | undefined;
  /** Where Nexa's purchase of the chosen flight is (Duffel / Atlas): refused, being
   *  recovered, verified, re-offered… A ticket outranks it; nothing else does — a refused
   *  purchase never reads as «ticket pending». */
  purchase?: PurchasePhase | null | undefined;
}

const TRIP_TONE: Record<string, { tone: BoardTone; pulse: boolean; key: string }> = {
  REQUESTED: { tone: 'warn', pulse: true, key: 'requested' },
  DISPATCHED: { tone: 'info', pulse: true, key: 'dispatched' },
  IN_TRANSIT: { tone: 'info', pulse: true, key: 'inTransit' },
  COMPLETED: { tone: 'ok', pulse: false, key: 'completed' },
  FAILED: { tone: 'alert', pulse: false, key: 'failed' },
  FAILED_COMPENSATED: { tone: 'alert', pulse: false, key: 'failed' },
  CANCELLED: { tone: 'idle', pulse: false, key: 'cancelled' },
};

/** i18n key segment for an offer bundle kind (`steps.passenger.offerKind.*`). */
export function offerKindKey(kind: string | undefined): string {
  return (kind && OFFER_KIND_KEY[kind]) || 'other';
}

const OFFER_KIND_KEY: Record<string, string> = {
  FLIGHT_AND_HOTEL: 'flightAndHotel',
  REBOOKING: 'flight',
  HOTEL: 'hotel',
  MEAL_VOUCHER: 'mealVoucher',
  TRANSFER: 'transfer',
  OTHER: 'other',
};

const DASH = '—';

function flightStep(i: StepsInput, t: PnrT): StepState {
  const { group, liveStatus } = i;
  const flight = i.assignedFlightNumber ?? group.assignedFlight?.number;
  const disrupted = [group.flightNumber, i.disruptedWhen].filter(Boolean).join(' · ');
  const assignedPrimary = [flight, i.assignedRoute].filter(Boolean).join(' · ');
  const assignedSecondary = [i.assignedWhen, i.recovery?.label].filter(Boolean).join(' · ');
  const seat = group.assignedFlight?.seat
    ? t('steps.flight.seat', { seat: group.assignedFlight.seat })
    : t('steps.flight.seatAtCheckIn');

  // Care-only + no flight chosen: the flight is an OFFERING here — the passenger
  // may already hold one booked elsewhere. Idle "Own arrangements", never the
  // amber "Awaiting flight" pulse and never the POLICY_DISABLED reason copy (a
  // lie on a declared care-only case). A kept SELECTED/ticketed flight still
  // renders its real branches below. When the airline's manifest named the
  // next flight, THAT is what the step shows — it is a fact we were given.
  if (careOnlyNoFlight(i)) {
    const manifest = group.manifestNextFlight;
    if (manifest) {
      const route = manifest.origin && manifest.destination ? `${manifest.origin} → ${manifest.destination}` : undefined;
      const when = i.formatWhen(manifest.departureUtc);
      return {
        key: 'flight',
        tone: 'idle',
        pulse: false,
        value: `${manifest.flightNumber} · ${when}`,
        state: t('steps.flight.state.fromManifest'),
        assigned: { primary: [manifest.flightNumber, route].filter(Boolean).join(' · '), secondary: when },
        delivered: { primary: t('steps.flight.delivered.fromManifest'), secondary: disrupted },
      };
    }
    return {
      key: 'flight',
      tone: 'idle',
      pulse: false,
      value: t('steps.flight.value.ownArrangements'),
      state: t('steps.flight.state.ownArrangements'),
      assigned: { primary: t('steps.flight.assigned.ownArrangements'), secondary: disrupted },
      delivered: { primary: DASH },
    };
  }
  if (liveStatus === 'NOT_APPLICABLE') {
    return {
      key: 'flight',
      tone: 'idle',
      pulse: false,
      value: t('steps.flight.value.notApplicable'),
      state: t('steps.flight.state.notApplicable'),
      assigned: { primary: i.noOptionsCopy ?? t('steps.flight.assigned.notApplicable'), secondary: disrupted },
      delivered: { primary: DASH },
    };
  }
  if (i.isTicketed) {
    return {
      key: 'flight',
      tone: 'ok',
      pulse: false,
      value: t('steps.flight.value.ticketed', { flight: flight ?? DASH }),
      state: t('steps.flight.state.ticketed'),
      assigned: { primary: assignedPrimary || DASH, secondary: assignedSecondary || undefined },
      delivered: {
        primary: t('steps.flight.delivered.ticket', { ticket: i.ticketNumber ?? DASH }),
        secondary: seat,
      },
    };
  }
  if (i.purchase) {
    return purchaseFlightStep(i.purchase, { flight, assignedPrimary, assignedSecondary, disrupted }, t);
  }
  if (i.isSelected || liveStatus === 'SELECTED' || liveStatus === 'OVERRIDDEN') {
    const overridden = liveStatus === 'OVERRIDDEN';
    return {
      key: 'flight',
      tone: 'ok',
      pulse: false,
      value: t('steps.flight.value.selected', { flight: flight ?? DASH }),
      state: overridden ? t('steps.flight.state.overridden') : t('steps.flight.state.selected'),
      // Chosen, not issued yet: under way, never «Listo».
      phase: 'inProgress',
      assigned: { primary: assignedPrimary || DASH, secondary: assignedSecondary || undefined },
      delivered: {
        primary: t('steps.flight.delivered.ticketPending'),
        secondary: t('steps.flight.delivered.ticketPendingHint'),
      },
    };
  }
  if (i.externalBooking) {
    // #771 — resolved on the external rail: the step reads ok with the vendor
    // locator, never the amber "pending" demand. Sandbox mode is said out loud.
    const provider = i.externalBooking.provider === 'duffel' ? 'Duffel' : 'Atlas';
    const reference = i.externalBooking.bookingReference ?? DASH;
    return {
      key: 'flight',
      tone: 'ok',
      pulse: false,
      value: t('steps.flight.value.externalPurchased', { provider, reference }),
      state: t('steps.flight.state.externalPurchased'),
      assigned: { primary: t('steps.flight.assigned.externalPurchased', { provider }), secondary: disrupted },
      delivered: {
        primary: t('steps.flight.delivered.externalReference', { reference }),
        secondary:
          i.externalBooking.mode === 'sandbox' ? t('steps.flight.delivered.externalSandbox') : undefined,
      },
    };
  }
  if (liveStatus === 'NO_OPTIONS') {
    return {
      key: 'flight',
      tone: 'alert',
      pulse: false,
      value: t('steps.flight.value.noOptions'),
      state: t('steps.flight.state.noOptions'),
      assigned: { primary: i.noOptionsCopy ?? t('steps.flight.assigned.noOptions'), secondary: disrupted },
      delivered: { primary: DASH, secondary: t('steps.flight.delivered.noTicket') },
    };
  }
  if (liveStatus === 'DEFERRED') {
    return {
      key: 'flight',
      tone: 'idle',
      pulse: false,
      value: t('steps.flight.value.deferred'),
      state: t('steps.flight.state.deferred'),
      phase: 'pending',
      assigned: { primary: t('steps.flight.assigned.deferred'), secondary: disrupted },
      delivered: { primary: DASH, secondary: t('steps.flight.delivered.noTicket') },
    };
  }
  // Every option left (PO 2026-11-03): never «elige una de 5 opciones» over flights that are gone.
  if (i.optionsExpired) {
    const count = i.optionsCount || group.optionsCount;
    return {
      key: 'flight',
      tone: 'alert',
      pulse: false,
      value: t('steps.flight.value.expired'),
      state: t('steps.flight.state.expired'),
      phase: 'needsDecision',
      assigned: { primary: t('steps.flight.assigned.expired', { count }), secondary: disrupted },
      delivered: { primary: DASH, secondary: t('steps.flight.delivered.noTicket') },
    };
  }
  if (liveStatus === 'OPTIONS_READY' || i.optionsCount > 0 || group.optionsCount > 0) {
    const count = i.optionsCount || group.optionsCount;
    return {
      key: 'flight',
      tone: 'info',
      pulse: true,
      value: t('steps.flight.value.options', { count }),
      state: t('steps.flight.state.options'),
      phase: 'needsDecision',
      assigned: { primary: t('steps.flight.assigned.choose', { count }), secondary: disrupted },
      delivered: { primary: DASH, secondary: t('steps.flight.delivered.noTicket') },
    };
  }
  // PENDING (and anything unforeseen) — nothing ranked yet.
  return {
    key: 'flight',
    tone: 'warn',
    pulse: true,
    value: t('steps.flight.value.pending'),
    state: t('steps.flight.state.pending'),
    assigned: { primary: t('steps.flight.assigned.pending'), secondary: disrupted },
    delivered: { primary: DASH, secondary: t('steps.flight.delivered.noTicket') },
  };
}

/** The phase a purchase reads as on the trip: an operator's call, under way, or done. */
const PURCHASE_PHASE: Record<PurchasePhase, { tone: BoardTone; pulse: boolean; phase: TripPhase }> = {
  REVIEW: { tone: 'alert', pulse: false, phase: 'needsDecision' },
  NEEDS_OPERATOR: { tone: 'alert', pulse: false, phase: 'needsDecision' },
  SEARCHING: { tone: 'info', pulse: true, phase: 'inProgress' },
  AUTO_BUYING: { tone: 'info', pulse: true, phase: 'inProgress' },
  BUYING: { tone: 'info', pulse: true, phase: 'inProgress' },
  REOFFERED: { tone: 'warn', pulse: true, phase: 'inProgress' },
  CONFIRMING: { tone: 'warn', pulse: true, phase: 'inProgress' },
  RECOVERED: { tone: 'ok', pulse: false, phase: 'done' },
};

/** The flight Nexa buys, while its purchase is refused, recovered, verified or running. */
function purchaseFlightStep(
  purchase: PurchasePhase,
  lines: { flight: string | undefined; assignedPrimary: string; assignedSecondary: string; disrupted: string },
  t: PnrT,
): StepState {
  const spec = PURCHASE_PHASE[purchase];
  return {
    key: 'flight',
    tone: spec.tone,
    pulse: spec.pulse,
    phase: spec.phase,
    value: t('steps.flight.value.purchase', {
      flight: lines.flight ?? DASH,
      state: t(`steps.flight.state.purchase.${purchase}`).toLowerCase(),
    }),
    state: t(`steps.flight.state.purchase.${purchase}`),
    assigned: { primary: lines.assignedPrimary || DASH, secondary: lines.assignedSecondary || lines.disrupted || undefined },
    delivered: {
      primary: purchase === 'RECOVERED' ? t('steps.flight.delivered.purchaseRecovered') : DASH,
      secondary: t(`steps.flight.delivered.purchase.${purchase}`),
    },
  };
}

function hotelStep(i: StepsInput, t: PnrT): StepState {
  const { group } = i;
  const { reservation, needs, sameDay, stay, stayingHome } = i.hotel;
  const station =
    group.overnightStation && group.overnightStation !== group.origin
      ? t('steps.hotel.atHub', { station: group.overnightStation })
      : t('steps.hotel.atStation', { station: group.origin });

  if (reservation) {
    const name = reservation.hotelName ?? reservation.hotelUrn;
    const rooms =
      typeof reservation.rooms === 'number' ? t('steps.hotel.rooms', { count: reservation.rooms }) : undefined;
    // The airline's price (commercial-contract §6.13); an unpriced row (`priced: false`,
    // `total: null`) reads «No disponible», never a number.
    const total =
      typeof reservation.total === 'number' && reservation.currency
        ? reservation.total.toLocaleString(undefined, { style: 'currency', currency: reservation.currency })
        : (reservation as { priced?: boolean }).priced === false
          ? t('option.priceUnavailable')
          : undefined;
    const secondary = [rooms, stay, total].filter(Boolean).join(' · ');
    if (reservation.status === 'BOOKED') {
      return {
        key: 'hotel',
        tone: 'ok',
        pulse: false,
        value: t('steps.hotel.value.booked', { stay: stay ?? name }),
        state: t('steps.hotel.state.booked'),
        assigned: { primary: name, secondary: secondary || undefined },
        delivered: { primary: t('steps.hotel.delivered.booked'), secondary: t('steps.hotel.delivered.bookedHint') },
      };
    }
    if (reservation.status === 'HELD') {
      return {
        key: 'hotel',
        tone: 'warn',
        pulse: false,
        value: t('steps.hotel.value.held', { stay: stay ?? name }),
        state: t('steps.hotel.state.held'),
        phase: 'inProgress',
        assigned: { primary: name, secondary: secondary || undefined },
        delivered: {
          primary: t('steps.hotel.delivered.held'),
          secondary: t('steps.hotel.delivered.heldHint'),
          // The commercial deadline only — never the soft inventory TTL (#783).
          acceptanceExpiresAt: reservation.acceptanceExpiresAt ?? '',
        },
      };
    }
    // PENDING — the booking is in flight.
    return {
      key: 'hotel',
      tone: 'warn',
      pulse: true,
      value: t('steps.hotel.value.pending'),
      state: t('steps.hotel.state.pending'),
      phase: 'inProgress',
      assigned: { primary: name, secondary: secondary || undefined },
      delivered: { primary: t('steps.hotel.delivered.pending') },
    };
  }
  // The passenger REJECTED the hotel. Their answer stands: no automatic re-hold,
  // the preference and their words are what the operator acts on (re-offer by hand).
  const declined = lastDecline(group);
  if (declined?.hotel) {
    return {
      key: 'hotel',
      tone: 'alert',
      pulse: false,
      value: t('steps.hotel.value.declined'),
      state: t('steps.hotel.state.declined'),
      assigned: {
        primary: declined.intent ? t(`hotelDecline.${declined.intent}`) : t('steps.hotel.assigned.declined'),
        secondary: declined.reason ? quoted(declined.reason) : undefined,
      },
      delivered: { primary: DASH, secondary: t('steps.hotel.delivered.declined') },
    };
  }
  // Operator-declared stay-home — the passenger sleeps at their own home. Calm,
  // no demand; undoable from the hotel card. Checked BEFORE sameDay/needs so the
  // suppressed `requiresHotel: false` never reads as "same-day re-accommodation".
  if (stayingHome) {
    return {
      key: 'hotel',
      tone: 'idle',
      pulse: false,
      value: t('steps.hotel.value.ownHome'),
      state: t('steps.hotel.state.ownHome'),
      assigned: { primary: t('steps.hotel.assigned.ownHome') },
      delivered: { primary: DASH },
    };
  }
  if (group.accommodationOutcome) {
    const outcome = group.accommodationOutcome.outcome;
    const key =
      outcome === 'SELF_MANAGED' ? 'selfManaged' : outcome === 'AIRPORT_WAIT' ? 'airportWait' : 'none';
    return {
      key: 'hotel',
      tone: 'idle',
      pulse: false,
      value: t(`steps.hotel.value.${key}`),
      state: t(`steps.hotel.state.${key}`),
      assigned: { primary: t(`steps.hotel.assigned.${key}`), secondary: group.accommodationOutcome.note },
      delivered: { primary: DASH },
    };
  }
  if (group.accommodationKind === 'MEAL_VOUCHER') {
    return {
      key: 'hotel',
      tone: 'idle',
      pulse: false,
      value: t('steps.hotel.value.voucherInstead'),
      state: t('steps.hotel.state.notNeeded'),
      assigned: { primary: t('steps.hotel.assigned.voucherInstead') },
      delivered: { primary: DASH },
    };
  }
  if (needs) {
    // Care-only: the stay is an OFFERING, not a demand — info tone, NO pulse,
    // "Optional — not booked". The same allocate actions stay available.
    if (i.careOnly) {
      return {
        key: 'hotel',
        tone: 'info',
        pulse: false,
        value: t('steps.hotel.value.optional'),
        state: t('steps.hotel.state.optional'),
        phase: 'pending',
        assigned: { primary: t('steps.hotel.assigned.optional'), secondary: [station, stay].filter(Boolean).join(' · ') },
        delivered: { primary: DASH, secondary: t('steps.hotel.delivered.noRoom') },
      };
    }
    return {
      key: 'hotel',
      tone: 'alert',
      pulse: true,
      value: t('steps.hotel.value.needed'),
      state: t('steps.hotel.state.needed'),
      assigned: { primary: t('steps.hotel.assigned.needed'), secondary: [station, stay].filter(Boolean).join(' · ') },
      delivered: { primary: DASH, secondary: t('steps.hotel.delivered.noRoom') },
    };
  }
  if (sameDay) {
    return {
      key: 'hotel',
      tone: 'idle',
      pulse: false,
      value: t('steps.hotel.value.sameDay'),
      state: t('steps.hotel.state.notNeeded'),
      assigned: { primary: t('steps.hotel.assigned.sameDay') },
      delivered: { primary: DASH },
    };
  }
  return {
    key: 'hotel',
    tone: 'idle',
    pulse: false,
    value: t('steps.hotel.value.afterFlight'),
    state: t('steps.hotel.state.afterFlight'),
    phase: 'pending',
    assigned: { primary: t('steps.hotel.assigned.afterFlight'), secondary: t('steps.hotel.assigned.afterFlightHint') },
    delivered: { primary: DASH },
  };
}

function mealsStep(i: StepsInput, t: PnrT): StepState {
  const { group, card, formatMoney } = i;
  const entitlement = group.mealVoucher;
  const entitlementLabel =
    typeof entitlement?.amountUsd === 'number'
      ? formatMoney(Math.round(entitlement.amountUsd * 100), entitlement.currency || 'USD')
      : undefined;
  const lounge = entitlement?.includesLounge ? t('steps.meals.loungeIncluded') : t('steps.meals.loungeNotIncluded');

  if (card) {
    const loaded = typeof card.loadedMinor === 'number' ? formatMoney(card.loadedMinor, card.currency) : entitlementLabel;
    const last4 = card.last4 ? `•••• ${card.last4}` : t('steps.meals.cardNoNumber');
    const available =
      card.balancesKnown && typeof card.availableMinor === 'number'
        ? t('steps.meals.available', { amount: formatMoney(card.availableMinor, card.currency) })
        : t('steps.meals.balanceUnknown');
    const active = card.status === 'ACTIVE' || card.status === 'ISSUED';
    // Care board rows include entitlements with NO card. Only an explicit
    // BLOCKED wallet state means blocked; async absence is not a failure (#734).
    const presentations: Record<string, { stateKey: string; tone: BoardTone; pulse: boolean }> = {
      ACTIVE: { stateKey: 'issued', tone: 'ok', pulse: false },
      ISSUED: { stateKey: 'issued', tone: 'ok', pulse: false },
      NOT_ISSUED: { stateKey: 'entitled', tone: 'info', pulse: false },
      PENDING: { stateKey: 'issuing', tone: 'info', pulse: true },
      BLOCKED_KYC: { stateKey: 'verificationRequired', tone: 'warn', pulse: false },
      FAILED: { stateKey: 'failed', tone: 'alert', pulse: false },
      FAILED_COMPENSATED: { stateKey: 'failed', tone: 'alert', pulse: false },
      BLOCKED: { stateKey: 'blocked', tone: 'alert', pulse: false },
      DISABLED: { stateKey: 'disabled', tone: 'idle', pulse: false },
    };
    const { stateKey, tone, pulse } = presentations[card.status]
      ?? { stateKey: 'unknown', tone: 'idle', pulse: false };
    const phase: TripPhase | undefined =
      stateKey === 'entitled' ? 'pending' : stateKey === 'unknown' ? 'pending' : undefined;
    const notDelivered = ['NOT_ISSUED', 'BLOCKED_KYC', 'PENDING'].includes(card.status);
    return {
      key: 'meals',
      tone,
      pulse,
      phase,
      value: active
        ? t('steps.meals.value.issued', { amount: loaded ?? DASH, last4 })
        : stateKey === 'entitled'
          ? t('steps.meals.value.entitled', { amount: loaded ?? DASH })
          : t(`steps.meals.value.${stateKey}`),
      state: t(`steps.meals.state.${stateKey}`),
      assigned: {
        primary: t('steps.meals.assigned.allowance', { amount: loaded ?? DASH }),
        secondary: entitlement ? t('steps.meals.assigned.policy', { lounge }) : notDelivered ? undefined : t('steps.meals.assigned.discretionary'),
      },
      delivered: notDelivered
        ? { primary: DASH, secondary: t('steps.meals.delivered.notIssued') }
        : stateKey === 'unknown'
          ? { primary: DASH, secondary: t('steps.meals.value.unknown') }
          : { primary: t('steps.meals.delivered.card', { last4 }), secondary: available },
    };
  }
  if (group.careOutcome) {
    return {
      key: 'meals',
      tone: 'idle',
      pulse: false,
      value: t('steps.meals.value.settledOutside'),
      state: t('steps.meals.state.settledOutside'),
      assigned: { primary: entitlementLabel ? t('steps.meals.assigned.allowance', { amount: entitlementLabel }) : DASH },
      delivered: { primary: t('steps.meals.delivered.settledOutside') },
    };
  }
  if (entitlement && entitlementLabel) {
    return {
      key: 'meals',
      tone: 'warn',
      pulse: true,
      value: t('steps.meals.value.entitled', { amount: entitlementLabel }),
      state: t('steps.meals.state.entitled'),
      assigned: {
        primary: t('steps.meals.assigned.allowance', { amount: entitlementLabel }),
        secondary: t('steps.meals.assigned.policy', { lounge }),
      },
      delivered: { primary: DASH, secondary: t('steps.meals.delivered.notIssued') },
    };
  }
  return {
    key: 'meals',
    tone: 'idle',
    pulse: false,
    value: i.isSelected || i.isTicketed ? t('steps.meals.value.none') : t('steps.meals.value.afterFlight'),
    state: i.isSelected || i.isTicketed ? t('steps.meals.state.none') : t('steps.meals.state.afterFlight'),
    phase: i.isSelected || i.isTicketed ? 'notApplicable' : 'pending',
    assigned: {
      primary: i.isSelected || i.isTicketed ? t('steps.meals.assigned.none') : t('steps.meals.assigned.afterFlight'),
      secondary: t('steps.meals.assigned.noneHint'),
    },
    delivered: { primary: DASH },
  };
}

function transportStep(i: StepsInput, t: PnrT): StepState {
  const { group } = i;
  const legLabel = (leg: string): string =>
    leg === 'HOTEL_TO_AIRPORT'
      ? t('steps.transport.legHotelToAirport')
      : leg === 'AIRPORT_TO_HOME'
        ? t('steps.transport.legAirportToHome')
        : t('steps.transport.legAirportToHotel');

  if (group.transportOutcome?.outcome === 'OWN_MEANS') {
    return {
      key: 'transport',
      tone: 'idle',
      pulse: false,
      value: t('steps.transport.value.ownMeans'),
      state: t('steps.transport.state.ownMeans'),
      assigned: { primary: t('steps.transport.assigned.ownMeans'), secondary: group.transportOutcome.note },
      delivered: { primary: DASH },
    };
  }
  const live = i.trips.mine.find((tr) => !isTerminalTripStatus(tr.status));
  // The Uber voucher IS the transport — the passenger rides whenever they want. Only a
  // car actually on the way reads before it; a FAILED car or shuttle seat it stands in
  // for is history, never "viaje fallido".
  const voucher = live ? undefined : voucherStep(i, t, legLabel);
  if (voucher) return voucher;
  // Oldest first (`sortTrips`): the latest terminal trip is the last one.
  const latest = live ?? i.trips.mine[i.trips.mine.length - 1];
  if (latest) {
    const spec = TRIP_TONE[latest.status] ?? { tone: 'idle' as BoardTone, pulse: false, key: 'cancelled' };
    const driver = latest.driverInfo
      ? [latest.driverInfo.name, latest.driverInfo.vehicle, latest.driverInfo.licensePlate].filter(Boolean).join(' · ')
      : undefined;
    const eta =
      typeof latest.pickupEtaMinutes === 'number'
        ? t('steps.transport.eta', { minutes: latest.pickupEtaMinutes })
        : latest.scheduledAt
          ? t('steps.transport.pickupAt', { when: i.formatWhen(latest.scheduledAt) })
          : undefined;
    const count = i.trips.mine.length;
    return {
      key: 'transport',
      tone: spec.tone,
      pulse: spec.pulse,
      phase: spec.key === 'requested' ? 'inProgress' : spec.key === 'cancelled' ? 'notApplicable' : undefined,
      value: t(`steps.transport.value.${spec.key}`, { count }),
      state: t(`steps.transport.state.${spec.key}`),
      assigned: {
        primary: legLabel(latest.leg),
        secondary: [latest.providerName, count > 1 ? t('steps.transport.rides', { count }) : undefined]
          .filter(Boolean)
          .join(' · '),
      },
      delivered: {
        primary: driver ?? (live ? t('steps.transport.delivered.vehiclePending') : DASH),
        secondary: eta ?? latest.lastError,
      },
    };
  }
  if (group.transfer) {
    const tr = group.transfer;
    const spec = TRIP_TONE[tr.status] ?? { tone: 'info' as BoardTone, pulse: false, key: 'dispatched' };
    const run = tr.kind === 'SHUTTLE' && typeof tr.runSeq === 'number' ? t('steps.transport.shuttleRun', { seq: tr.runSeq }) : undefined;
    return {
      key: 'transport',
      tone: spec.tone,
      pulse: spec.pulse,
      value: run ?? t(`steps.transport.value.${spec.key}`, { count: 1 }),
      state: t(`steps.transport.state.${spec.key}`),
      assigned: { primary: legLabel(tr.leg), secondary: run },
      delivered: {
        primary: tr.boardingStatus ? t(`steps.transport.boarding.${tr.boardingStatus.toLowerCase()}`) : DASH,
        secondary: tr.pickupTime ? t('steps.transport.pickupAt', { when: i.formatWhen(tr.pickupTime) }) : undefined,
      },
    };
  }
  if (!i.trips.loaded) {
    return {
      key: 'transport',
      tone: 'idle',
      pulse: false,
      value: t('steps.transport.value.loading'),
      state: t('steps.transport.state.loading'),
      phase: 'pending',
      assigned: { primary: DASH },
      delivered: { primary: DASH },
    };
  }
  // NO DESTINATION, NO TRANSPORT. A ride needs a CONFIRMED hotel or the declared
  // stay-home — a HELD room may still be declined or released. Without one the
  // step is idle and says what it waits for, never the amber "not arranged".
  const destination = transportDestination(group, i.hotel.reservation);
  // Stay-home (ride-home vertical): the passenger needs no hotel, so no transfer
  // is EXPECTED — but with a geocoded home a ride home is on offer. Idle, not
  // amber: the operator may dispatch it, nothing is late without it. Without a
  // point there is nowhere to drive to and the step says so.
  if (destination === 'HOME') {
    const offered = !!group.homeDestination?.coordinates;
    return {
      key: 'transport',
      tone: 'idle',
      pulse: false,
      value: offered ? t('steps.transport.value.rideHomeOffered') : t('steps.transport.value.notNeeded'),
      state: offered ? t('steps.transport.state.rideHomeOffered') : t('steps.transport.state.notNeeded'),
      assigned: offered
        ? { primary: legLabel('AIRPORT_TO_HOME'), secondary: t('steps.transport.assigned.rideHome') }
        : { primary: t('steps.transport.assigned.stayHomeNoDestination') },
      delivered: { primary: DASH, secondary: offered ? t('steps.transport.delivered.none') : undefined },
    };
  }
  if (destination === 'HOTEL') {
    return {
      key: 'transport',
      tone: 'warn',
      pulse: false,
      value: t('steps.transport.value.notArranged'),
      state: t('steps.transport.state.notArranged'),
      assigned: { primary: legLabel('AIRPORT_TO_HOTEL'), secondary: t('steps.transport.assigned.expected') },
      delivered: { primary: DASH, secondary: t('steps.transport.delivered.none') },
    };
  }
  // A hotel is needed, held, or on offer (care-only) — but not confirmed yet.
  if (i.hotel.reservation || i.hotel.needs || i.careOnly) {
    return {
      key: 'transport',
      tone: 'idle',
      pulse: false,
      value: t('steps.transport.value.awaitingDestination'),
      state: t('steps.transport.state.awaitingDestination'),
      phase: 'pending',
      assigned: {
        primary: t('steps.transport.assigned.awaitingDestination'),
        secondary: i.hotel.reservation ? t('steps.transport.assigned.hotelPending') : undefined,
      },
      delivered: { primary: DASH },
    };
  }
  return {
    key: 'transport',
    tone: 'idle',
    pulse: false,
    value: t('steps.transport.value.notNeeded'),
    state: t('steps.transport.state.notNeeded'),
    assigned: { primary: t('steps.transport.assigned.onlyWithHotel') },
    delivered: { primary: DASH },
  };
}

/**
 * The transport step when the group's Uber voucher counts (`voucherCoversTransport`):
 * green once ACTIVE, sky-blue while the codes are being issued. Reads the drawer's live
 * voucher; until that read lands, the roster's `transportVoucher` fact (no usage marks).
 */
function voucherStep(i: StepsInput, t: PnrT, legLabel: (leg: string) => string): StepState | undefined {
  const full = i.voucher;
  const fact = full === undefined ? i.group.transportVoucher : undefined;
  if (full !== undefined ? !voucherCoversTransport(full) : !rosterVoucherCounts(fact)) return undefined;
  const v = (full ?? fact)!;
  const issuing = v.status === 'ISSUING' || v.status === 'CODES_PENDING';
  const live = full ? full.assignments.filter((a) => !a.cancelledAt) : undefined;
  const codes = live ? live.length : (fact?.paxUrns.length ?? 0);
  const redeemed = live?.filter((a) => a.redeemedAt).length ?? 0;
  const claimed = live?.filter((a) => a.claimedAt).length ?? 0;
  const key = issuing ? 'voucherIssuing' : 'voucher';
  return {
    key: 'transport',
    tone: issuing ? 'info' : 'ok',
    pulse: issuing,
    value: t(`steps.transport.value.${key}`),
    state: t(`steps.transport.state.${key}`),
    assigned: {
      primary:
        v.scenario === 'HOTEL_OVERNIGHT'
          ? t('steps.transport.legRoundTripHotel')
          : legLabel(v.scenario === 'RIDE_HOME' ? 'AIRPORT_TO_HOME' : 'AIRPORT_TO_HOTEL'),
      secondary: codes > 0 ? t('steps.transport.voucherCodes', { count: codes }) : undefined,
    },
    delivered: {
      primary: !live
        ? DASH
        : redeemed > 0
          ? t('steps.transport.delivered.voucherRedeemed', { count: redeemed })
          : claimed > 0
            ? t('steps.transport.delivered.voucherClaimed', { count: claimed })
            : t('steps.transport.delivered.voucherUnused'),
      secondary: issuing ? undefined : t('steps.transport.voucherUntil', { when: i.formatWhen(new Date(v.endsAt).toISOString()) }),
    },
  };
}

function passengerStep(i: StepsInput, t: PnrT): StepState {
  const { group, notification: row } = i;
  const kindKey = group.offer ? (OFFER_KIND_KEY[group.offer.kind] ?? 'other') : undefined;
  const offerLabel = kindKey ? t(`steps.passenger.offerKind.${kindKey}`) : t('steps.passenger.offerKind.other');
  const proposed = row?.lastEventAt ? t('steps.passenger.proposedAt', { when: i.formatWhen(row.lastEventAt) }) : undefined;
  const channel = row?.channel ? t(`steps.passenger.channel.${row.channel.toLowerCase()}`) : undefined;
  const deliveryWord = row ? t(`delivery.state.${row.deliveryState}`) : undefined;
  const sentLine = [channel ? t('steps.passenger.sentBy', { channel }) : undefined, deliveryWord]
    .filter(Boolean)
    .join(' · ');

  if (!row || row.acceptanceState === 'none') {
    // Status updates are sent before any offer exists. Delivery evidence and
    // the action communicated in that message are independent of the live gate.
    const delivery = row?.deliveryState ?? 'unknown';
    const reason = notificationFailureReason(row?.failureReason);
    const action = row?.requestedAction === 'CONTACT_DESK' ? 'contactDesk'
      : row?.requestedAction === 'ACCEPT_OFFER' ? 'actionRequired' : 'noActionRequired';
    return {
      key: 'passenger',
      tone: delivery === 'failed' ? 'alert' : delivery === 'delivered' ? 'ok'
        : delivery === 'unknown' ? 'idle' : 'info',
      pulse: delivery === 'pending',
      value: t(`notification.current.${delivery}`),
      state: t(`notification.current.${delivery}`),
      assigned: {
        primary: t('notification.current.title'),
        secondary: t(`notification.current.${delivery === 'unknown' ? 'sendAvailable' : action}`),
      },
      delivered: {
        primary: t(`notification.current.${delivery}`),
        secondary: delivery === 'failed' ? t(`notification.current.failure.${reason}`)
          : [channel, row?.lastEventAt ? i.formatWhen(row.lastEventAt) : undefined].filter(Boolean).join(' · ') || undefined,
      },
    };
  }
  if (row.acceptanceState === 'accepted') {
    return {
      key: 'passenger',
      tone: 'ok',
      pulse: false,
      value: t('steps.passenger.value.accepted'),
      state: t('steps.passenger.state.accepted'),
      assigned: { primary: offerLabel, secondary: proposed },
      delivered: {
        primary: t('steps.passenger.delivered.accepted'),
        secondary: row.lastEventAt ? i.formatWhen(row.lastEventAt) : undefined,
      },
    };
  }
  if (row.acceptanceState === 'declined') {
    const declined = lastDecline(group);
    return {
      key: 'passenger',
      tone: 'alert',
      pulse: false,
      value: t(declined?.hotel ? 'steps.passenger.value.hotelDeclined' : 'steps.passenger.value.declined'),
      state: t('steps.passenger.state.declined'),
      assigned: { primary: offerLabel, secondary: proposed },
      delivered: declined?.intent || declined?.reason
        ? {
          primary: declined.intent ? t(`hotelDecline.${declined.intent}`) : t('steps.passenger.delivered.declined'),
          secondary: declined.reason ? quoted(declined.reason) : t('steps.passenger.delivered.declinedHint'),
        }
        : { primary: t('steps.passenger.delivered.declined'), secondary: t('steps.passenger.delivered.declinedHint') },
    };
  }
  // A response proves delivery, so accepted/declined are read first; a failure
  // only matters while the passenger has not been reached at all.
  if (row.deliveryState === 'failed') {
    return {
      key: 'passenger',
      tone: 'alert',
      pulse: false,
      value: t('steps.passenger.value.deliveryFailed'),
      state: t('steps.passenger.state.deliveryFailed'),
      assigned: { primary: offerLabel, secondary: proposed },
      delivered: { primary: sentLine || t('delivery.state.failed'), secondary: t('steps.passenger.delivered.fixContact') },
    };
  }
  // The passenger saw the offer, got into the app or opened the link: it reached
  // them — green, still awaiting the answer, said as strongly as we know it.
  const seen = passengerSeen(row);
  if (seen) {
    return {
      key: 'passenger',
      tone: 'ok',
      pulse: true,
      value: t(`steps.passenger.value.${seen.kind}`),
      state: t(`steps.passenger.state.${seen.kind}`),
      assigned: { primary: offerLabel, secondary: proposed },
      delivered: {
        primary: t(`steps.passenger.delivered.${seen.kind}`, { when: i.formatWhen(seen.at) }),
        secondary: t('steps.passenger.delivered.awaitingHint'),
      },
    };
  }
  // awaiting
  return {
    key: 'passenger',
    tone: 'warn',
    pulse: true,
    value: t('steps.passenger.value.awaiting'),
    state: t('steps.passenger.state.awaiting'),
    assigned: { primary: offerLabel, secondary: proposed },
    delivered: {
      primary: sentLine || t('delivery.state.sent'),
      secondary: t('steps.passenger.delivered.awaitingHint'),
    },
  };
}

/** Every step, in journey order. */
/** Care-only and no flight of ours: nothing selected, ticketed or bought. */
function careOnlyNoFlight(i: StepsInput): boolean {
  return (
    !!i.careOnly &&
    !i.externalBooking &&
    !i.isTicketed &&
    !i.isSelected &&
    i.liveStatus !== 'SELECTED' &&
    i.liveStatus !== 'OVERRIDDEN'
  );
}

/**
 * Care-only with no flight to show: none of ours, and the airline's manifest
 * named none. The airline re-accommodates — there is no Flight step to draw,
 * so the board and the ledger have no Flight row at all.
 */
export function flightStepHidden(i: StepsInput): boolean {
  return careOnlyNoFlight(i) && !i.group.manifestNextFlight;
}

export function deriveSteps(input: StepsInput, t: PnrT): StepState[] {
  return [
    ...(flightStepHidden(input) ? [] : [flightStep(input, t)]),
    hotelStep(input, t),
    mealsStep(input, t),
    transportStep(input, t),
    passengerStep(input, t),
  ];
}

/* ── Next up ───────────────────────────────────────────────────────────────────
 * The ONE thing to do now, precedence-ordered from "the passenger has no flight"
 * down to "all set". `kind` tells the dialog which action to put in its bar:
 * the mutations for select / generate / ticket / resend live on the dialog;
 * the tab-shaped kinds (hotel / card / transport) point at the step that owns
 * the action. */

export type NextUpKind =
  | 'purchase'
  | 'noOptions'
  | 'generate'
  | 'select'
  | 'hotel'
  | 'acceptance'
  | 'ticket'
  | 'card'
  | 'transport'
  | 'done';

export interface NextUp {
  kind: NextUpKind;
  tone: BoardTone;
  title: string;
  body: string;
  /** The label of the one primary action, when the kind has one. */
  primaryLabel?: string | undefined;
  /** For `select`: the recommended option's flight designator. */
  recommendedFlight?: string | undefined;
  /** For `purchase`: where Nexa's purchase of the flight is. */
  purchase?: PurchasePhase | undefined;
  /** For `acceptance`: the message never arrived, the offer awaits, it was declined, or
   *  declined asking for a call (`CALLBACK_REQUESTED`). */
  cause?: 'deliveryFailed' | 'awaiting' | 'declined' | 'callback' | undefined;
}

export interface NextUpInput {
  liveStatus: RosterRebookingStatus;
  isSelected: boolean;
  isTicketed: boolean;
  optionsCount: number;
  noOptionsCopy?: string | undefined;
  recommendedFlight?: string | undefined;
  hotelNeeded: boolean;
  hotelReserved: boolean;
  /** A wallet card exists (any live status). */
  cardIssued: boolean;
  cardEntitled: boolean;
  transportArranged: boolean;
  transportSettled: boolean;
  acceptance: 'none' | 'awaiting' | 'accepted' | 'declined' | undefined;
  deliveryFailed: boolean;
  /** The passenger's stated preference on their last decline (`hotelDecline.*`). */
  declineIntent?: string | undefined;
  /** The passenger rejected the hotel: no "book the hotel" demand — they said no. */
  hotelDeclined?: boolean | undefined;
  /** Care-only case: flight/hotel are offerings — never the ONE thing to do.
   *  The derivation skips those demands and falls through to
   *  deliveryFailed/acceptance/card/transport/done. */
  careOnly?: boolean | undefined;
  /** #771 — a CONFIRMED external (LCC) purchase exists: the flight demand is
   *  settled on the external rail, so generate/select/noOptions never surface;
   *  the derivation falls through to hotel/acceptance/… like a resolved flight. */
  externalPurchased?: boolean | undefined;
  /** Where Nexa's purchase of the chosen flight is (Duffel / Atlas). While refused,
   *  recovered, verified or running, THAT is the next thing — never «issue the ticket». */
  purchase?: PurchasePhase | null | undefined;
  /** The chosen flight is one Nexa buys (Duffel / Atlas): nothing issues it but its
   *  purchase, so an accepted, unpurchased flight never reads «se emite solo». */
  nexaBuys?: boolean | undefined;
}

const PURCHASE_NEXT_TONE: Record<PurchasePhase, BoardTone> = {
  REVIEW: 'alert',
  NEEDS_OPERATOR: 'alert',
  SEARCHING: 'info',
  AUTO_BUYING: 'info',
  BUYING: 'info',
  REOFFERED: 'warn',
  CONFIRMING: 'warn',
  RECOVERED: 'ok',
};

export function deriveNextUp(i: NextUpInput, t: PnrT): NextUp {
  // A purchase in trouble (or running) outranks everything but a ticket: the band says
  // where it is, never «the ticket is issuing itself» (pnr-steps ~1069, PO 2026-10-31).
  if (i.purchase && !i.isTicketed && i.purchase !== 'RECOVERED') {
    return {
      kind: 'purchase',
      tone: PURCHASE_NEXT_TONE[i.purchase],
      // The flight step's own words: the band overlays the recovery's detail on top.
      title: t(`steps.flight.state.purchase.${i.purchase}`),
      body: t(`steps.flight.delivered.purchase.${i.purchase}`),
      purchase: i.purchase,
    };
  }
  // Care-only skips every flight/hotel DEMAND: no "generate options", no
  // "allocate the hotel", no noOptions dead-end — those are offerings the
  // operator MAY arrange (their actions stay on the cards/bar). A kept SELECTED
  // flight still surfaces its ticket step below.
  const skipFlightDemands = i.careOnly === true || i.externalPurchased === true;
  if (!skipFlightDemands && (i.liveStatus === 'NOT_APPLICABLE' || i.liveStatus === 'NO_OPTIONS')) {
    return {
      kind: 'noOptions',
      tone: i.liveStatus === 'NO_OPTIONS' ? 'alert' : 'idle',
      title: i.noOptionsCopy ?? t('nextUp.noOptions.title'),
      body: t('nextUp.noOptions.body'),
    };
  }
  if (!i.isSelected && !i.isTicketed) {
    if (skipFlightDemands) {
      // Nothing selected on a care-only case: fall through to the passenger-side
      // steps (delivery/acceptance/card/transport/done) — never demand a flight.
    } else if (i.optionsCount > 0) {
      return {
        kind: 'select',
        tone: 'info',
        title: t('nextUp.select.title'),
        body: t('nextUp.select.body', { count: i.optionsCount }),
        primaryLabel: i.recommendedFlight
          ? t('nextUp.select.primary', { flight: i.recommendedFlight })
          : t('nextUp.select.primaryNoFlight'),
        recommendedFlight: i.recommendedFlight,
      };
    } else {
      return {
        kind: 'generate',
        tone: 'warn',
        title: t('nextUp.generate.title'),
        body: t('nextUp.generate.body'),
        primaryLabel: t('nextUp.generate.primary'),
      };
    }
  }
  if (!i.careOnly && !i.hotelDeclined && i.hotelNeeded && !i.hotelReserved) {
    return {
      kind: 'hotel',
      tone: 'alert',
      title: t('nextUp.hotel.title'),
      body: t('nextUp.hotel.body'),
      primaryLabel: t('nextUp.hotel.primary'),
    };
  }
  if (i.deliveryFailed) {
    return {
      kind: 'acceptance',
      cause: 'deliveryFailed',
      tone: 'alert',
      title: t('nextUp.deliveryFailed.title'),
      body: t('nextUp.deliveryFailed.body'),
      primaryLabel: t('nextUp.acceptance.primary'),
    };
  }
  if (i.acceptance === 'awaiting') {
    return {
      kind: 'acceptance',
      cause: 'awaiting',
      tone: 'warn',
      title: t('nextUp.acceptance.title'),
      body: t('nextUp.acceptance.body'),
      primaryLabel: t('nextUp.acceptance.primary'),
    };
  }
  if (i.acceptance === 'declined') {
    return {
      kind: 'acceptance',
      cause: i.declineIntent === 'CALLBACK_REQUESTED' ? 'callback' : 'declined',
      tone: 'alert',
      title: t('nextUp.declined.title'),
      body:
        i.declineIntent === 'CALLBACK_REQUESTED'
          ? t('hotelDecline.CALLBACK_REQUESTED')
          : i.declineIntent
            ? t('nextUp.declined.bodyIntent', { intent: t(`hotelDecline.${i.declineIntent}`) })
            : t('nextUp.declined.body'),
      primaryLabel: t('nextUp.acceptance.primary'),
    };
  }
  // Care-only without a selection falls through here — a ticket demand with no
  // flight would be nonsense. A KEPT selected flight still asks for its ticket.
  if (!i.isTicketed && (i.isSelected || !i.careOnly)) {
    return {
      kind: 'ticket',
      tone: 'warn',
      title: t('nextUp.ticket.title'),
      body:
        i.acceptance !== 'accepted'
          ? t('nextUp.ticket.body')
          : i.nexaBuys
            ? t('nextUp.ticket.bodyAcceptedNexa')
            : t('nextUp.ticket.bodyAccepted'),
      primaryLabel: t('nextUp.ticket.primary'),
    };
  }
  if (i.cardEntitled && !i.cardIssued) {
    return {
      kind: 'card',
      tone: 'warn',
      title: t('nextUp.card.title'),
      body: t('nextUp.card.body'),
      primaryLabel: t('nextUp.card.primary'),
    };
  }
  if (i.hotelReserved && !i.transportArranged && !i.transportSettled) {
    return {
      kind: 'transport',
      tone: 'warn',
      title: t('nextUp.transport.title'),
      body: t('nextUp.transport.body'),
      primaryLabel: t('nextUp.transport.primary'),
    };
  }
  return {
    kind: 'done',
    tone: 'ok',
    title: t('nextUp.done.title'),
    body: t('nextUp.done.body'),
  };
}
